import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import useThirdPartyInvoice from "../../hooks/useThirdPartyInvoice";
import useData from "../../hooks/useData";
import ThirdPartyInvoiceEditor from "../editors/ThirdPartyInvoiceEditor";
import Select from "../elements/Select";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const ThirdPartyInvoicesList = ({ onRefresh }) => {
  const { invoices, currentInvoice, getInvoices, deleteInvoice } =
    useThirdPartyInvoice();
  const { getFromServer } = useData();

  const [selectedClientProjectId, setSelectedClientProjectId] = useState(null);
  const [openEditor, setOpenEditor] = useState(false);
  const [openClientProjectSelector, setOpenClientProjectSelector] =
    useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [tempClientId, setTempClientId] = useState("");
  const [tempProjectId, setTempProjectId] = useState("");
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    loadInvoices();
    loadClientsAndProjects();
  }, []);

  const loadClientsAndProjects = async () => {
    const clientData = await getFromServer({
      entityName: "Client",
      filter: { isActive: true },
    });
    setClients(clientData || []);

    const projectData = await getFromServer({
      entityName: "Project",
      filter: { isActive: true },
    });
    setProjects(projectData || []);
  };

  const loadInvoices = async () => {
    await getInvoices();
  };

  const handleNewInvoice = () => {
    setTempClientId("");
    setTempProjectId("");
    setOpenClientProjectSelector(true);
  };

  const handleConfirmClientProject = () => {
    if (tempClientId && tempProjectId) {
      setSelectedClientProjectId(`${tempClientId}_${tempProjectId}`);
      setOpenClientProjectSelector(false);
      setOpenEditor(true);
    } else {
      alert("Please select both client and project");
    }
  };

  const handleEditInvoice = (invoice) => {
    setSelectedClientProjectId(`${invoice.clientId}_${invoice.projectId}`);
    setOpenEditor(true);
  };

  const handleCloseEditor = () => {
    setOpenEditor(false);
    setSelectedClientProjectId(null);
    loadInvoices();
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    await deleteInvoice(deleteConfirmId);
    await loadInvoices();
    setDeleteConfirmId(null);
  };

  const groupedByClient = invoices.reduce((acc, inv) => {
    const clientName = inv.client?.name || "Unknown Client";
    if (!acc[clientName]) {
      acc[clientName] = { clientId: inv.clientId, invoices: [] };
    }
    acc[clientName].invoices.push(inv);
    return acc;
  }, {});

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <Box
        display="flex"
        justifyContent="flex-end"
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleNewInvoice}
        >
          New Third-Party Invoice
        </Button>
      </Box>

      {Object.entries(groupedByClient).length > 0 ? (
        Object.entries(groupedByClient).map(
          ([clientName, { clientId, invoices: clientInvoices }]) => (
            <Card key={clientId}>
              <CardContent>
                <Typography
                  variant="h6"
                  marginBottom={2}
                >
                  {clientName}
                </Typography>

                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>Project</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Lines</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          {invoice.project?.name || "Unknown"}
                        </TableCell>
                        <TableCell>
                          {invoice.vendor?.name || "Unknown"}
                        </TableCell>
                        <TableCell align="right">
                          ${(invoice.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{invoice.lines?.length || 0}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={() => handleEditInvoice(invoice)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirmId(invoice.id)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        )
      ) : (
        <Typography
          color="textSecondary"
          align="center"
          padding={4}
        >
          No third-party invoices found
        </Typography>
      )}

      {/* Editor dialog */}
      <Dialog
        open={openEditor}
        onClose={handleCloseEditor}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Third-Party Invoice</DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <ThirdPartyInvoiceEditor
            clientId={selectedClientProjectId?.split("_")[0]}
            projectId={selectedClientProjectId?.split("_")[1]}
            onClose={handleCloseEditor}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog_YesNo
        open={Boolean(deleteConfirmId)}
        title="Delete Invoice?"
        message="Are you sure you want to delete this third-party invoice?"
        onYes={handleDelete}
        onNo={() => setDeleteConfirmId(null)}
      />

      {/* Client/Project Selector Dialog */}
      <Dialog
        open={openClientProjectSelector}
        onClose={() => setOpenClientProjectSelector(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Client and Project</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 2,
          }}
        >
          <Select
            label="Client"
            items={clients}
            value={tempClientId}
            onChange={setTempClientId}
            required
          />
          <Select
            label="Project"
            items={projects.filter(
              (p) => !tempClientId || p.clientId === tempClientId
            )}
            value={tempProjectId}
            onChange={setTempProjectId}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenClientProjectSelector(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmClientProject}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThirdPartyInvoicesList;
